import { LoanSetupScreen } from "@/screens/loans/components/setup/LoanSetupScreen"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { useUser } from "@/queries/user"
import type { PreQualificationType, UserType } from "@/generated/graphql"
import { useEffect, useMemo } from "react"
import { $isNewLoan } from "@/stores/createLoan"
import type { SummaryItemProps } from "@/screens/loans/components/SummaryItem"
import {
  buildBorrowerDetailsItems,
  buildContractDetailItems,
  buildLoanDetailsItems,
  buildPropertyInfoItems,
  loanAppSuccessPath,
} from "@/screens/loans/utils/LoanUtils"
import { Vertical } from "@/components/standard/BFLayout"
import { LoanDetailsDisclaimer } from "@/screens/loans/components/LoanDetailsDisclaimer"
import { SummarySection } from "@/screens/loans/components/SummarySection"
import { router } from "expo-router"
import { NegativeROIWarning } from "@/screens/loans/components/NegativeROIWarning"
import { EstimatedTotalsSection } from "@/screens/loans/components/EstimatedTotalsSection"
import { AvailableDiscountsSectionV2 } from "@/screens/loans/components/AvailableDiscountsSectionV2"
import { Divider } from "@/screens/loans/components/Divider"
import { ApplyAndSubmitSection } from "@/screens/loans/components/ApplyAndSubmitSection"
import {
  loanAppErrorEncountered,
  loanApplicationSubmitted,
} from "@/analytics/events/loans"
import { formatDate } from "@/utils/DateUtils"
import { showErrorToast } from "@/components/toasts/AppToast"
import type { ClientError } from "graphql-request"
import { useEditLoan } from "@/queries/loan"
import { fbqTrackCustom, fbqUserAttributes } from "@/utils/PixelUtils"

export const ReviewAndSubmitScreen = ({
  backButton,
}: {
  backButton?: () => void
}) => {
  const { loanApplication, loanId } = useLoanContext()
  const { data: userData } = useUser()
  const user = userData?.me || ({} as UserType)
  const updateLoanMutation = useEditLoan()
  const isRewardsEligible = userData?.me?.rewardsEligible ?? false
  const isRewardsOptedIn = userData?.me?.rewardsOptInStatus ?? false
  const rewardsDealCount = userData?.me?.rewardsDealCount || 0

  const preQualState = userData?.currentPreQualification as PreQualificationType
  const fbqAttributes = fbqUserAttributes(preQualState, userData)

  const costBasis =
    (loanApplication.purchasePrice || 0) +
    (loanApplication.rehabEstimate || 0) +
    (loanApplication.loanProduct === "refinance"
      ? loanApplication.constructionCostsSpent || 0
      : 0)
  const grossProfit = (loanApplication.afterRepairValue || 0) - costBasis
  const showRewardsDiscount = isRewardsEligible && isRewardsOptedIn

  useEffect(() => {
    // Reset the new loan flag when the user navigates away from the review and submit tab
    $isNewLoan.set(false)
  }, [])

  const borrowerDetailsItems = useMemo<SummaryItemProps[]>(() => {
    return buildBorrowerDetailsItems(user, loanApplication)
  }, [loanApplication, user])

  const contractDetailsItems = useMemo<SummaryItemProps[]>(() => {
    return buildContractDetailItems(loanApplication)
  }, [loanApplication])

  const propertyInfoItems = useMemo<SummaryItemProps[]>(() => {
    return buildPropertyInfoItems(loanApplication)
  }, [loanApplication])

  const loanDetailsItems = useMemo<SummaryItemProps[]>(() => {
    return buildLoanDetailsItems(
      loanApplication,
      showRewardsDiscount ? Math.min(rewardsDealCount, 5) * 0.1 : null
    )
  }, [loanApplication, showRewardsDiscount, rewardsDealCount])

  const doSubmit = async () => {
    if (loanApplication) {
      loanApplicationSubmitted({
        propertyAddress: loanApplication.property.fullAddress,
        arv: loanApplication.afterRepairValue,
        purchasePrice: loanApplication.purchasePrice,
        rehabCost: loanApplication.rehabEstimate,
        earnestMoneyDeposit: loanApplication.earnestMoneyDeposit,
        rewardsCredit: loanApplication.rewardsCredit,
        cashToClose: loanApplication.estimatedCashToClose,
        leverage: loanApplication.loanToCost,
        loanAmount: loanApplication.totalLoanAmount,
        interestRate: loanApplication.interestRate,
        moPayment: loanApplication.monthlyPayment,
        financingFees: loanApplication.financingFee,
        origFee: loanApplication.originationRate,
        borrower: loanApplication.borrower,
        flips: loanApplication.flipsCompleted,
        rentals: loanApplication.rentalProperties,
        cash: loanApplication.cashAvailable,
        creditScore: loanApplication.creditScore,
        contractStatus: loanApplication.contractStatus,
        closingDate: loanApplication.targetCloseDate
          ? formatDate(loanApplication.targetCloseDate, "yyyy-M-d", true)
          : null,
      })
      updateLoanMutation.mutate(
        {
          loanId,
          loanInput: {
            status: "Submitted",
          },
        },
        {
          onSuccess: () => {
            fbqTrackCustom("LAppSubmitted", fbqAttributes)
            router.push(loanAppSuccessPath(loanId))
          },
          onError: (err) => {
            console.error(err)
            const displayMessage =
              "Please give us a call at 844.363.5477 to continue."
            showErrorToast({
              title: "Processing Error",
              message: displayMessage,
            })
            loanAppErrorEncountered({
              error: err as ClientError,
              displayedMessage: displayMessage,
              propertyAddress:
                loanApplication?.property.fullAddress || "Unknown",
              userId: String(userData?.me?.id),
              userName:
                userData?.me?.firstName + " " + userData?.me?.lastName ||
                "Unknown",
            })
          },
        }
      )
    }
  }

  return (
    <LoanSetupScreen
      title={"Review & Submit"}
      stepData={[4, 4]}
      nextButtonProps={{
        title: "Get Loan Quote",
        disabled:
          loanApplication.loanProduct === undefined ||
          updateLoanMutation.isPending,
        variant: "primary",
      }}
      onSubmit={doSubmit}
      backButton={backButton}
    >
      <Vertical>
        <Vertical className="gap-0 px-4 pb-1 my-0">
          <LoanDetailsDisclaimer />
        </Vertical>
        <Vertical className={"p-4 gap-4"}>
          <EstimatedTotalsSection />
          <AvailableDiscountsSectionV2 />
          {grossProfit < 0 && <NegativeROIWarning />}
          <SummarySection
            title="Loan Product"
            items={loanDetailsItems}
            onEdit={() => {
              router.push("./product")
            }}
          />
          <SummarySection
            title="Property Info"
            items={propertyInfoItems}
            toolLineIndex={2}
            onEdit={() => {
              router.push("./property")
            }}
          />
          <SummarySection
            title="Contract Details"
            items={contractDetailsItems}
            onEdit={() => {
              router.push("./property")
            }}
          />
          <SummarySection
            title="Borrower Details"
            items={borrowerDetailsItems}
            onEdit={() => {
              router.push("./qualifications")
            }}
          />
        </Vertical>
        <Divider />
        <ApplyAndSubmitSection />
      </Vertical>
    </LoanSetupScreen>
  )
}
